/* eslint-disable jsx-a11y/no-redundant-roles */
/* eslint-disable jsx-a11y/role-supports-aria-props */
import { HeadPhoneGuideModal } from '@/components/common/head-phone-guide-modal';
import { VoiceGuideModal } from '@/components/common/modal-voice-guide';
import { SpeakerCloseModal } from '@/components/common/speaker-close-modal';
import { ZoomDialog } from '@/components/common/zoom-dialog';
import { ClockIcon } from '@/components/icons/ClockIcon';
import FooterBackIcon from '@/components/icons/footer/BackIcon';
import GlobalIcon from '@/components/icons/GlobalIcon';
import { useAccessibilityTTS, useDropdownTTS, useTickSoundInteraction, useVolumeButton } from '@/hooks';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useMainFocusTrapControl } from '@/hooks/useMainFocusTrapControl';
import { PATHS } from '@/router/paths';
import { TrackingService } from '@/services/analytics/TrackingService';
import { useConfigStore, useFnbStore, useShopStore, type languageType } from '@/stores';
import { CONFIG_CONSTANTS, defaultConfig, buildMenuFromBuildingMenus, getOperatingHoursText, getRequestStaffHelpFromBuildingMenus } from '@/stores/configStore';
import type { GNBItemType } from '@/types';
import { formatLocalClockTime, formatOperatingHours, getActivityTimeTTS } from '@/utils/time';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './FooterLayout.module.scss';
import { AppUtils } from '@/utils/app';
import { NAV_KEYS } from '@/constants/keyboard';
import { DownIcon, UpIcon } from '@/components/icons';
interface FooterLayoutProps {
    onZoomButtonsOpen?: (isOpen: boolean) => void;
    isZoomButtonsOpen?: boolean;
    handleZoomClose?: () => void;
    onBarrierFreeUsed?: () => void;
    closeModal?: (closeFunction: () => void) => void;
    onBarrierFreeReset?: () => void;
}

export const FooterLayout: React.FC<FooterLayoutProps> = ({
    onZoomButtonsOpen,
    isZoomButtonsOpen,
    handleZoomClose,
    onBarrierFreeUsed,
    closeModal,
    onBarrierFreeReset,
}) => {
    // built-in hooks
    const location = useLocation();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const ui = useConfigStore((state) => state.config.ui);
    const openTime = useConfigStore((state) => state.config.ui.openTime);
    const closeTime = useConfigStore((state) => state.config.ui.closeTime);
    const operatingHoursEnabled = useConfigStore((state) => state.config.ui.operatingHoursEnabled);
    const operatingHoursMode = useConfigStore((state) => state.config.ui.operatingHoursMode);
    const operatingHoursText = useConfigStore((state) => state.config.ui.operatingHoursText);
    const tts = useConfigStore((state) => state.config.tts);
    const voice = useConfigStore((state) => state?.config?.tts?.voice);
    const zoomIn = useConfigStore((state) => state?.config?.ui?.zoomIn);
    const server = useConfigStore((state) => state.config.server);
    const buildingMenus = useConfigStore((state) => state.config.server?.buildingMenus);
    const lotteEvents = useConfigStore((state) => state.config.server?.building?.lotteEvents);
    const updateConfig = useConfigStore((state) => state.updateConfig);
    const setFnbKeyboardLanguage = useFnbStore((state) => state.setKeyboardLanguage);
    const setShopKeyboardLanguage = useShopStore((state) => state.setKeyboardLanguage);
    const language = useConfigStore((state) => state.config.ui.language);
    const isScreenSaverFullscreen = useConfigStore((state) => state.config?.ui?.isScreenSaverFullscreen);
    const storeLangs = useConfigStore((state) => state.config?.server?.store?.storeLangs);
    // custom hooks
    const { trapFocus, releaseFocus } = useFocusTrap();
    const { disableMainFocusTrap, enableMainFocusTrap } = useMainFocusTrapControl();
    const { speakOnFocus } = useDropdownTTS();
    const { speak } = useAccessibilityTTS();
    const { playTick } = useTickSoundInteraction();
    const { volume, handleVolumeChange, handleVolumeFocus, handleVolumeKeyDown, isVolumeChanged } = useVolumeButton(onBarrierFreeUsed);

    // local states
    const [isZoomDialogOpen, setIsZoomDialogOpen] = useState(false);
    const [isShowHeadPhoneGuide, setIsShowHeadPhoneGuide] = useState(false);
    const [isShowSpeakerClose, setIsShowSpeakerClose] = useState(false);
    const [isShowVoiceGuide, setIsShowVoiceGuide] = useState(false);
    const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
    const [currentLocalTime, setCurrentLocalTime] = useState(() => new Date());

    // refs
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const speakTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const configTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isHomePage = location.pathname === PATHS.HOME.INDEX;

    useEffect(() => {
        const updateCurrentLocalTime = () => setCurrentLocalTime(new Date());
        const now = new Date();
        const delayUntilNextMinute = 60_000 - (now.getSeconds() * 1_000 + now.getMilliseconds());
        let intervalId: number | undefined;
        const timeoutId = window.setTimeout(() => {
            updateCurrentLocalTime();
            intervalId = window.setInterval(updateCurrentLocalTime, 60_000);
        }, delayUntilNextMinute);

        return () => {
            window.clearTimeout(timeoutId);
            if (intervalId !== undefined) window.clearInterval(intervalId);
        };
    }, []);

    // Get TTS parameters for activity time
    const activityTimeTTSParams = useMemo(() => getActivityTimeTTS(openTime, closeTime, language), [openTime, closeTime, language]);

    const activeUiLang = i18n.language as languageType;

    const languageButtonLabel = useMemo(() => {
        const entry = storeLangs?.find((lang) => lang.code === language);
        const rawName = entry?.display_name?.trim();
        return rawName || AppUtils.getLanguageLabelByActiveLang(language, activeUiLang);
    }, [storeLangs, language, activeUiLang]);

    const resolveLanguageOptionLabel = useCallback(
        (option: { label: string; value: languageType }) => {
            const entry = storeLangs?.find((lang) => lang.code === option.value);
            const rawName = entry?.display_name?.trim();
            return rawName || AppUtils.getLanguageLabelByActiveLang(option.value, activeUiLang);
        },
        [storeLangs, activeUiLang]
    );

    // Memoized handlers
    const handleConfigUpdate = useCallback(
        (updates: Partial<typeof ui>) => {
            updateConfig({ ui: { ...ui, ...updates } });
        },
        [ui, updateConfig]
    );

    const handleContrastToggle = useCallback(() => {
        handleConfigUpdate({ contrast: !ui.contrast });
        onBarrierFreeUsed?.();
    }, [ui.contrast, handleConfigUpdate]);

    const handleScreenModeToggle = useCallback(() => {
        const newMode = ui.screenMode === 'fullscreen' ? 'split' : 'fullscreen';
        handleConfigUpdate({ screenMode: newMode });
        onBarrierFreeUsed?.();
    }, [ui.screenMode, handleConfigUpdate, onBarrierFreeUsed]);

    const handleZoomToggle = useCallback(() => {
        if (isZoomButtonsOpen) {
            handleZoomClose?.();
            return;
        }
        setIsZoomDialogOpen(true);
        onBarrierFreeUsed?.();
        TrackingService.onHomePopupOpen();
    }, [isZoomButtonsOpen, handleZoomClose, onBarrierFreeUsed]);

    const handleVoiceChange = useCallback(() => {
        if (tts.voice) {
            if (tts.speaker === 'headphone') {
                setIsShowHeadPhoneGuide(true);
            } else {
                setIsShowSpeakerClose(true);
            }
        } else {
            if (!tts.voice) {
                updateConfig({ tts: { ...tts, voice: true } });
                setIsShowVoiceGuide(true);
                onBarrierFreeUsed?.();
            }
        }
    }, [tts, onBarrierFreeUsed]);

    const handleOnConfirmSpeakerCloseModal = useCallback(() => {
        setIsShowSpeakerClose(false);
        const currentLanguage = (storeLangs?.find((lang) => lang.main_lang)?.code as languageType) || CONFIG_CONSTANTS.DEFAULT_LANGUAGE;
        // Get menu items using buildMenuFromBuildingMenus
        const menu: GNBItemType[] = buildMenuFromBuildingMenus(buildingMenus, currentLanguage, currentLanguage);

        // Change i18n language to Korean
        i18n.changeLanguage(currentLanguage);

        updateConfig({
            ui: {
                ...ui,
                language: currentLanguage,
                menu,
                requestStaffHelp: getRequestStaffHelpFromBuildingMenus(buildingMenus, currentLanguage, server),
                zoomIn: false,
                zoomClosed: true,
                contrast: false,
                volume: 3,
                screenMode: 'fullscreen',
                zoomLevel: 1,
            },
            tts: {
                ...tts,
                voice: false,
            },
        });

        // Reset keyboard language to Korean
        setFnbKeyboardLanguage(currentLanguage === 'ko' ? 'ko' : 'en');
        setShopKeyboardLanguage(currentLanguage === 'ko' ? 'ko' : 'en');

        onZoomButtonsOpen?.(false);
        navigate(PATHS.HOME.INDEX);
    }, [tts, ui, buildingMenus, lotteEvents, i18n, updateConfig, navigate, onZoomButtonsOpen, setFnbKeyboardLanguage, setShopKeyboardLanguage, storeLangs]);

    const handleOnConfirmVoiceGuideModal = useCallback(() => {
        setIsShowVoiceGuide(false);
        updateConfig({ tts: { ...tts, voice: true } });
        navigate(PATHS.HOME.INDEX);
    }, [tts, updateConfig, navigate]);

    const handleOnCloseVoiceGuideModal = () => {
        setIsShowVoiceGuide(false);
        updateConfig({ tts: { ...tts, voice: false } });
    };
    const handleOnConfirmAndNotRedirectHome = () => {
        setIsShowVoiceGuide(false);
        updateConfig({ tts: { ...tts, voice: true } });
    };

    // Custom TTS for language selection
    const speakLanguageSelection = useCallback(
        (languageLabel: string, lang: languageType) => {
            if (!voice) return;
            const ttsText = i18n.t('footer.barrier_free.language_selected', { language: languageLabel, lng: lang });
            speak(ttsText, lang);
        },
        [voice, speak, i18n]
    );

    const handleLanguageSelect = useCallback(
        (langValue: string, optionLabel: string) => {
            // Clear any existing timeouts
            if (speakTimeoutRef.current) {
                clearTimeout(speakTimeoutRef.current);
            }
            if (configTimeoutRef.current) {
                clearTimeout(configTimeoutRef.current);
            }

            const mainLanguage = (storeLangs?.find((lang) => lang.main_lang)?.code as languageType) || CONFIG_CONSTANTS.DEFAULT_LANGUAGE;
            const menu: GNBItemType[] = buildMenuFromBuildingMenus(buildingMenus, langValue, mainLanguage);

            // Update language & menu immediately so App.tsx won't revert i18n and GNB labels update before TTS
            handleConfigUpdate({
                language: langValue as 'ko' | 'en' | 'ja' | 'zh-CN' | 'zh-TW',
                menu,
                requestStaffHelp: getRequestStaffHelpFromBuildingMenus(buildingMenus, langValue, server),
            });

            i18n.changeLanguage(langValue);

            onBarrierFreeUsed?.();

            onZoomButtonsOpen?.(false);
            TrackingService.onLocaleChanged();

            setIsLanguageDropdownOpen(false);

            if (langValue === 'ko') {
                setFnbKeyboardLanguage('ko');
                setShopKeyboardLanguage('ko');
            } else {
                setFnbKeyboardLanguage('en');
                setShopKeyboardLanguage('en');
            }

            speakTimeoutRef.current = setTimeout(() => {
                speakLanguageSelection(optionLabel, langValue as languageType);
            }, 50);

            configTimeoutRef.current = setTimeout(() => {
                // Use updateConfig directly to avoid stale ui closure overwriting language
                updateConfig({
                    ui: {
                        zoomIn: false,
                        zoomClosed: true,
                        contrast: false,
                        screenMode: 'fullscreen',
                        volume: 3,
                        zoomLevel: 1,
                    },
                });

                if (isScreenSaverFullscreen && !isHomePage) {
                    navigate(PATHS.FLOOR_GUIDE.INDEX);
                } else {
                    navigate(PATHS.HOME.INDEX);
                }
            }, 1200);
        },
        [
            buildingMenus,
            storeLangs,
            handleConfigUpdate,
            updateConfig,
            navigate,
            speakLanguageSelection,
            onZoomButtonsOpen,
            onBarrierFreeUsed,
            i18n,
            isScreenSaverFullscreen,
            isHomePage,
        ]
    );

    const handleToggleDropdown = useCallback(() => {
        if (!isLanguageDropdownOpen) {
            setIsLanguageDropdownOpen(true);
        } else {
            setIsLanguageDropdownOpen(false);
        }
    }, [isLanguageDropdownOpen, releaseFocus]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (!isLanguageDropdownOpen) {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setIsLanguageDropdownOpen(true);
                }
                return;
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                setIsLanguageDropdownOpen(false);
                releaseFocus();
            }
        },
        [isLanguageDropdownOpen, releaseFocus]
    );

    const handleZoomDialogClose = () => {
        setIsZoomDialogOpen(false);
        onZoomButtonsOpen?.(false);
        TrackingService.onHomePopupClose();
        onBarrierFreeReset?.();
    };

    const handleZoomDialogConfirm = () => {
        onBarrierFreeUsed?.();
        handleConfigUpdate({ zoomIn: !zoomIn, zoomClosed: false, zoomLevel: 2 });
        setIsZoomDialogOpen(false);
        onZoomButtonsOpen?.(true);
        TrackingService.onHomePopupClose();
    };

    // Function to close all modals - used by CountdownButton
    const handleCloseAllModals = useCallback(() => {
        setIsZoomDialogOpen(false);
        setIsShowHeadPhoneGuide(false);
        setIsShowSpeakerClose(false);
        setIsShowVoiceGuide(false);
        setIsLanguageDropdownOpen(false);
    }, []);

    // Expose closeModal function to parent component
    useEffect(() => {
        if (closeModal) {
            closeModal(handleCloseAllModals);
        }
    }, [closeModal, handleCloseAllModals]);

    // Close dropdown when clicking outside and handle focus
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsLanguageDropdownOpen(false);
                releaseFocus();
            }
        };

        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isLanguageDropdownOpen) {
                setIsLanguageDropdownOpen(false);
                releaseFocus();
            }
        };

        if (isLanguageDropdownOpen) {
            // Focus first option
            const firstOptionButton = dropdownRef.current?.querySelector('button[role="button"]') as HTMLButtonElement;
            if (firstOptionButton) {
                firstOptionButton.focus();
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscapeKey);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isLanguageDropdownOpen, releaseFocus]);

    useEffect(() => {
        if (isLanguageDropdownOpen && dropdownRef.current) {
            disableMainFocusTrap();
            trapFocus(dropdownRef.current, isLanguageDropdownOpen);
        } else {
            releaseFocus();
        }
    }, [isLanguageDropdownOpen, trapFocus, releaseFocus, disableMainFocusTrap, enableMainFocusTrap]);

    const AccessibilityButtons = useMemo(() => {
        return (
            <div
                className="flex items-center gap-[calc(12*var(--vw-unit))]"
                data-region-nav={isHomePage ? true : undefined}
                data-region-order={isHomePage ? 2 : undefined}
            >
                {ui.barrierFree && (
                    <button
                        type="button"
                        role="switch"
                        aria-checked={tts?.voice !== defaultConfig.tts.voice}
                        aria-valuetext={t('footer.barrier_free.voice_tts')}
                        data-playsound="ting"
                        data-valuenow={tts?.voice}
                        data-interactive
                        onClick={handleVoiceChange}
                        className={styles['footer__actions__button']}
                    >
                        <span className={styles['footer__actions__button__label']}>{t('footer.barrier_free.voice')}</span>
                    </button>
                )}

                <button
                    data-tts-scope="volume-button"
                    type="button"
                    role="switch"
                    data-role={t('common.button.tts.toggle_button')}
                    aria-checked={isVolumeChanged}
                    aria-label={t('footer.barrier_free.volume')}
                    data-valuenow={volume}
                    data-volumn-level={volume}
                    data-interactive
                    onClick={handleVolumeChange}
                    onFocus={handleVolumeFocus}
                    onKeyDown={handleVolumeKeyDown}
                    className={styles['footer__actions__button']}
                >
                    <span className={styles['footer__actions__button__label']}>{t('footer.barrier_free.volume') + ` ${volume}`}</span>
                </button>

                <button
                    type="button"
                    role="switch"
                    aria-checked={ui.contrast}
                    data-valuenow={ui.contrast}
                    aria-valuetext={t('footer.barrier_free.contrast_tts')}
                    data-interactive
                    onClick={handleContrastToggle}
                    className={styles['footer__actions__button']}
                >
                    <span className={styles['footer__actions__button__label']}>{t('footer.barrier_free.contrast')}</span>
                </button>

                <button
                    type="button"
                    role="switch"
                    data-role={t('common.button.tts.switch_button')}
                    aria-checked={ui.zoomIn}
                    aria-valuetext={t('footer.barrier_free.zoom_in_tts')}
                    data-interactive
                    data-playsound="ting"
                    onClick={handleZoomToggle}
                    className={styles['footer__actions__button']}
                >
                    <span className={styles['footer__actions__button__label']}>{t('footer.barrier_free.zoom_in')}</span>
                </button>

                <button
                    type="button"
                    role="switch"
                    aria-checked={ui.screenMode !== 'fullscreen'}
                    aria-valuetext={t('footer.barrier_free.screen_mode_tts')}
                    data-interactive
                    onClick={handleScreenModeToggle}
                    className={styles['footer__actions__button']}
                >
                    <span className={styles['footer__actions__button__label']}>{t('footer.barrier_free.screen_mode')}</span>
                </button>
            </div>
        );
    }, [
        ui.barrierFree,
        ui.contrast,
        ui.screenMode,
        ui.zoomIn,
        volume,
        tts,
        t,
        handleContrastToggle,
        handleScreenModeToggle,
        handleZoomToggle,
        handleVolumeChange,
        handleVoiceChange,
        isHomePage,
    ]);

    const displayedOperatingHours = operatingHoursEnabled
        ? operatingHoursMode === 'DIRECT_INPUT'
            ? getOperatingHoursText(operatingHoursText, ui.language, ui.mainLanguage)
            : formatOperatingHours(openTime, closeTime)
        : formatLocalClockTime(currentLocalTime);
    const activityTimeAriaLabel =
        operatingHoursEnabled && operatingHoursMode === 'TIME_SETTING'
            ? `${t('footer.activity_time')} ${t('footer.activity_time_tts', activityTimeTTSParams)}`
            : `${t('footer.activity_time')} ${displayedOperatingHours}`;

    return (
        <footer className={styles['footer']} role="contentinfo" data-nav="footer">
            <div className={styles['footer__content']}>
                {isHomePage ? (
                    <div className={styles['footer__info']}>
                        <div
                            role="list"
                            tabIndex={0}
                            className={styles['footer__info__open'] + ' focus-border'}
                            data-region-nav
                            data-region-order={1}
                            aria-label={activityTimeAriaLabel}
                            data-interactive
                        >
                            <ClockIcon />
                            <span className={styles['footer__info__open__time']}>
                                {operatingHoursEnabled && (
                                    <span className={styles['footer__info__open__time--label']}>{t('footer.activity_time')}</span>
                                )}
                                <span
                                    className={clsx(
                                        styles['footer__info__open__time--hour'],
                                        operatingHoursEnabled && operatingHoursMode === 'DIRECT_INPUT' && styles['footer__info__open__time--multiline']
                                    )}
                                >
                                    {displayedOperatingHours}
                                </span>
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className={styles['footer__actions']}>
                        <button
                            ref={buttonRef}
                            data-region-nav
                            type="button"
                            className={clsx(styles['footer__actions__button'], styles['back'])}
                            onClick={() => {
                                playTick();
                                navigate(-1);
                            }}
                            onKeyUp={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                                if (NAV_KEYS.includes(e.key)) {
                                    speak(t('common.data_role.screen_back'));
                                }
                            }}
                        >
                            <FooterBackIcon />
                            <span className={styles['footer__actions__button__label']}>{t('footer.back')}</span>
                        </button>
                    </div>
                )}
                <div className={styles['footer__actions']} data-region-nav={isHomePage ? undefined : true}>
                    {AccessibilityButtons}
                    <div
                        className={styles.language}
                        data-tts-scope="dropdown"
                        ref={dropdownRef}
                        data-region-nav={isHomePage ? true : undefined}
                        data-region-order={isHomePage ? 3 : undefined}
                    >
                        <button
                            ref={buttonRef}
                            type="button"
                            data-role={t('common.data_role.button')}
                            className={clsx(styles['footer__actions__button'])}
                            aria-label={t('footer.barrier_free.language')}
                            aria-expanded={isLanguageDropdownOpen}
                            data-interactive
                            onClick={handleToggleDropdown}
                            onKeyDown={handleKeyDown}
                            onFocus={(e) => {
                                speakOnFocus(e.target);
                            }}
                        >
                            <GlobalIcon />
                            <span className={clsx('font-number', styles['footer__actions__button__label'])}>{languageButtonLabel}</span>
                            {isLanguageDropdownOpen ? <UpIcon /> : <DownIcon />}
                        </button>

                        {isLanguageDropdownOpen && (
                            <div className={styles['language__options']} role="tablist" aria-label={t('footer.barrier_free.language')}>
                                {ui.languageOptions.map((option) => {
                                    const optionLabel = resolveLanguageOptionLabel(option);
                                    return (
                                        <button
                                            key={option.value}
                                            type="button"
                                            role="button"
                                            data-tts-scope="dropdown"
                                            aria-label={optionLabel}
                                            aria-checked={option.value === ui.language}
                                            aria-selected={option.value === ui.language}
                                            className={clsx(styles['language__options__item'], { [styles.selected]: option.value === ui.language })}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                handleLanguageSelect(option.value, optionLabel);
                                            }}
                                            onFocus={(e) => {
                                                speakOnFocus(e.target);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleLanguageSelect(option.value, optionLabel);
                                                }
                                            }}
                                            data-interactive
                                            tabIndex={0}
                                            disabled={!isLanguageDropdownOpen}
                                        >
                                            <span className={option.value === 'en' ? 'font-number' : ''}>{optionLabel}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <ZoomDialog isOpen={isZoomDialogOpen} onClose={handleZoomDialogClose} onConfirm={handleZoomDialogConfirm} />
            <SpeakerCloseModal isOpen={isShowSpeakerClose} onClose={() => setIsShowSpeakerClose(false)} onConfirm={handleOnConfirmSpeakerCloseModal} />
            <HeadPhoneGuideModal isOpen={isShowHeadPhoneGuide} onClose={() => setIsShowHeadPhoneGuide(false)} />
            <VoiceGuideModal
                isOpen={isShowVoiceGuide}
                onClose={handleOnCloseVoiceGuideModal}
                onConfirm={handleOnConfirmVoiceGuideModal}
                onConfirmAndNotRedirectHome={handleOnConfirmAndNotRedirectHome}
            />
        </footer>
    );
};

export default FooterLayout;
